import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Bike, ExternalLink, MapPin, Battery, Clock, Sparkles } from "lucide-react";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { SubPageNav } from "../components/SubPageNav";
import { fetchRestaurant } from "../lib/api";
import { T, tr } from "../lib/i18n";
import AppBottomBar from "../components/AppBottomBar";

// Interstitial cover page shown BEFORE sending the user to the external
// BikeSquare rental site. Keeps app branding (burgundy compact bar), lets the
// user cancel if they tapped by accident, and shows what to expect.
const EXTERNAL_URL = "https://rent.bikesquare.eu/maceratese/?ref_id=78460";

export default function BikeIntroPage() {
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");

  useEffect(() => { fetchRestaurant().then(setRestaurant).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);

  return (
    <div className="min-h-screen bg-[#faf3ee]" data-testid="bike-intro-page">
      <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar />

      <main className="max-w-3xl mx-auto px-4 py-6">
        <SubPageNav hideMenu lang={lang} />

        {/* Hero: bike video loop as background with warm overlay for readability */}
        <section className="relative overflow-hidden rounded-3xl bg-[#2b1410] text-[#f7ebdf] shadow-lg min-h-[320px] sm:min-h-[440px] flex items-center">
          {/* Background video */}
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/media/hero-bike.mp4"
            poster="/media/hero-bike-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-hidden="true"
          />
          {/* Warm burgundy overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#2b1410]/90 via-[#2b1410]/55 to-[#2b1410]/30 pointer-events-none" />
          {/* Grain flecks */}
          <div className="absolute inset-0 opacity-20 pointer-events-none mix-blend-overlay"
               style={{ backgroundImage: "radial-gradient(rgba(255,220,180,0.4) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />

          <div className="relative w-full px-6 py-10 sm:px-12 sm:py-14 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/25 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-[#e6c46a]">
              <Bike className="h-3.5 w-3.5" /> {tr(T.bikeTag, lang)}
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-6xl font-bold leading-[0.95] uppercase text-[#f7ebdf] drop-shadow-lg">
              {tr(T.bikeHeroLine1, lang)}<br /><span className="text-[#e6c46a] italic normal-case">{tr(T.bikeHeroLine2, lang)}</span>
            </h1>
            <p className="mt-5 max-w-md mx-auto text-sm sm:text-base text-[#f7ebdf]/95 drop-shadow">
              {tr(T.bikeHeroSubtitle, lang)}
            </p>
          </div>
        </section>

        {/* What you'll find on Bikesquare */}
        <section className="mt-6 grid sm:grid-cols-3 gap-3">
          <FeatureCard Icon={Battery} title={tr(T.bikeBatteryTitle, lang)} text={tr(T.bikeBatteryText, lang)} />
          <FeatureCard Icon={MapPin}  title={tr(T.bikeGpsTitle, lang)}     text={tr(T.bikeGpsText, lang)} />
          <FeatureCard Icon={Clock}   title={tr(T.bikeFlexTitle, lang)}    text={tr(T.bikeFlexText, lang)} />
        </section>

        {/* Awareness note + primary CTA */}
        <section className="mt-6 rounded-2xl bg-white border border-[#e8dcc8] shadow-sm p-5 sm:p-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#c48b31]/15 border border-[#c48b31]/30 px-3 py-1 text-[11px] font-medium text-[#4a1c17]">
            <Sparkles className="h-3.5 w-3.5" /> {tr(T.bikeLeavingHint, lang)}
          </div>
          <p className="mt-3 text-sm text-[#7c5a4e] max-w-lg mx-auto">
            {tr(T.bikePartnerText, lang)} <strong className="text-[#2b1410]">BikeSquare Maceratese</strong>. {tr(T.bikePartnerBack, lang)}
          </p>

          <a
            href={EXTERNAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bike-external-cta"
            className="mt-5 group inline-flex items-center justify-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] active:scale-[0.98] text-[#f7ebdf] px-6 py-3 sm:px-8 sm:py-3.5 text-sm sm:text-base font-semibold shadow-sm transition-all"
          >
            <span>{tr(T.bikeCta, lang)}</span>
            <ExternalLink className="h-4 w-4 opacity-90 group-hover:translate-x-0.5 transition-transform" />
          </a>

          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="bike-back"
            className="mt-3 block mx-auto text-xs text-[#7c5a4e] hover:text-[#4a1c17] underline"
          >
            {tr(T.goBack, lang)}
          </button>
        </section>
      </main>
      <AppBottomBar current="none" lang={lang} />
    </div>
  );
}

function FeatureCard({ Icon, title, text }) {
  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-4 flex flex-col items-start gap-2">
      <div className="h-9 w-9 rounded-full bg-[#4a1c17] text-[#f7ebdf] grid place-items-center">
        <Icon className="h-4 w-4" />
      </div>
      <div className="font-serif font-bold text-[#2b1410]">{title}</div>
      <div className="text-xs text-[#7c5a4e] leading-snug">{text}</div>
    </div>
  );
}
